import { useState, useEffect, useCallback } from 'react';
import cmsService from '../services/strapiService';
import { mockData } from '../mock';

// Custom hook for fetching CMS content with fallback to mock data
export const useCMSContent = (contentType, options = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usingFallback, setUsingFallback] = useState(false);

  const { category, pageSlug, sectionKey, fallbackData } = options;

  const fetchContent = useCallback(async () => {
    setLoading(true);
    setError(null);
    setUsingFallback(false);

    try {
      let result;
      
      switch (contentType) {
        case 'companyInfo':
          result = await cmsService.getCompanyInfo();
          break;
        case 'services':
          result = await cmsService.getServices();
          break;
        case 'testimonials':
          result = await cmsService.getTestimonials();
          break;
        case 'faqs':
          result = await cmsService.getFaqs(category);
          break;
        case 'ownerBenefits':
          result = await cmsService.getOwnerBenefits();
          break;
        case 'coreValues':
          result = await cmsService.getCoreValues();
          break;
        case 'serviceAreas':
          result = await cmsService.getServiceAreas();
          break;
        case 'announcements':
          result = await cmsService.getAnnouncements();
          break;
        case 'pageContent':
          result = await cmsService.getPageContent(pageSlug, sectionKey);
          break;
        default:
          throw new Error(`Unknown content type: ${contentType}`);
      }
      
      setData(result);
    } catch (err) {
      console.warn(`CMS fetch failed for ${contentType}, using fallback:`, err.message);
      setError(err);
      setUsingFallback(true);
      
      // Use fallback data from mock.js
      const fallback = fallbackData || getFallbackData(contentType);
      setData(fallback);
    } finally {
      setLoading(false);
    }
  }, [contentType, category, pageSlug, sectionKey, fallbackData]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  return { data, loading, error, usingFallback, refetch: fetchContent };
};

// Get fallback data from mock.js
const getFallbackData = (contentType) => {
  switch (contentType) {
    case 'companyInfo':
      return {
        companyName: mockData.company.name,
        email: mockData.company.email,
        phone: mockData.company.phone,
        fax: mockData.company.fax,
        businessHours: mockData.company.businessHours,
        tagline: mockData.company.tagline,
        heroTitle: mockData.hero.title,
        heroSubtitle: mockData.hero.subtitle,
        heroDescription: mockData.hero.description,
        ctaButtonText1: mockData.hero.cta1,
        ctaButtonText2: mockData.hero.cta2,
        missionStatement: mockData.about.mission,
        experienceDescription: mockData.about.experience
      };
    case 'services':
      return mockData.services.map((s, i) => ({
        id: s.id,
        title: s.title,
        description: s.description,
        icon: s.icon,
        order: i + 1
      }));
    case 'testimonials':
      return mockData.testimonials.map((t, i) => ({
        id: t.id,
        name: t.name,
        role: t.role,
        text: t.text,
        rating: t.rating,
        order: i + 1
      }));
    case 'faqs':
      return mockData.faq.map((f, i) => ({
        id: i + 1,
        question: f.question,
        answer: f.answer,
        category: 'General',
        order: i + 1
      }));
    case 'ownerBenefits':
      return mockData.ownerBenefits.map((b, i) => ({
        id: i + 1,
        title: b.title,
        description: b.description,
        order: i + 1
      }));
    case 'coreValues':
      return mockData.about.values.map((v, i) => ({
        id: i + 1,
        value: v,
        order: i + 1
      }));
    case 'serviceAreas':
      return mockData.serviceAreas.map((a, i) => ({
        id: i + 1,
        name: a,
        order: i + 1
      }));
    case 'announcements':
      return [];
    case 'pageContent':
      return [];
    default:
      return null;
  }
};

export default useCMSContent;
